/*eslint-disable @typescript-eslint/explicit-module-boundary-types*/

import FlattenUtil from 'flat';
import { formatPhoneNumber, unFormatPhoneNumber } from "@/app/utils/common/functions/functions";
import { OnboardingSectionData } from "@/store/modules/onboarding/onboardingTypes";
import { copyFromApiResponse, copyFromErrorResponse, prefillField } from "@/store/storeMappers";
import { validateAndGenerateError, validateAndSetValid } from "@/store/validationFromStore.js";
import Vue from "vue";
import { safeGetDomain } from '@/app/utils/common/functions/url-helper';
import { MerchantResponseDto, SessionPrefillData } from '@/api/paymentApi';

export enum BusinessProfileField {
    WEBSITE ="website",
    PHONE ="phone",
    DESCRIPTION ="description",
    MCC ="mcc",
    STATEMENT_DESCRIPTOR ="statementDescriptor"
}

export const incrementalOnboardingBusinessProfileDefaultState: OnboardingSectionData = {
    api: "createMerchant",
    errors: {},
    dirty: false,
    isPrefilled: false,
    data: {
        [BusinessProfileField.WEBSITE]: {
            apiPath: "business_info.url",
            value: null,
            valid: false,
            defaultValue: "https://",
        },
        [BusinessProfileField.PHONE]: {
            apiPath: "business_info.phone",
            value: null,
            valid: false,
        },
        [BusinessProfileField.DESCRIPTION]: {
            apiPath: "business_info.description",
            value: null,
            valid: false,
        },
        [BusinessProfileField.MCC]: {
            apiPath: "business_info.mcc",
            value: null,
            valid: false,
        },
        [BusinessProfileField.STATEMENT_DESCRIPTOR]: {
            apiPath: "processing_info.statement_descriptor",
            value: null,
            valid: false,
        },
    },
};

const getters = {
    isValid: (state) => {
        return Object.keys(state.data).every(key => state.data[key].valid);
    },
    getErrors: (state) => {
        return state.errors
    },
    isPrefilled: (state) => {
        return state.isPrefilled
    },
    totalInvalidFields: (s) => {
        return Object.keys(s.data).filter(key => s.data[key].valid === false).length
    },
    totalValidFields: (s) => {
        return Object.keys(s.data).filter(key => s.data[key].valid === true).length
    },
    isDirty: (s) => {
        return s.dirty
    },
    getField: (s) => {
        return (field: BusinessProfileField) => s.data[field].value ?? s.data[field].defaultValue
    },
    getCountryCode: (s) => {
        return s.data[BusinessProfileField.PHONE].value?.split(' ')[0]
    },
    getFieldFormatted: (s) => {
        return (field: BusinessProfileField) => s.data[field].formattedValue
    },
    getIncrementalOnboardingData: (s) => {
        return {
            url: s.data[BusinessProfileField.WEBSITE].value,
            phone: s.data[BusinessProfileField.PHONE].value,
            description: s.data[BusinessProfileField.DESCRIPTION].value,
            mcc: s.data[BusinessProfileField.MCC].value,
            statement_descriptor: s.data[BusinessProfileField.STATEMENT_DESCRIPTOR].value,
        }
    }
}

const actions = {
    resetErrors({commit}) {
        commit('resetErrors');
    },
    setErrors({commit}, {key, errors}) {
        commit('setErrors', {key, errors});
    },
    validate({commit, state, rootGetters}) {
        commit('resetErrors')
        return validateAndGenerateError(commit, state.api, rootGetters, rootGetters["essentials/getCountry"], state.data)
    },
    validateField({commit, state, rootGetters}, key) {
        validateAndSetValid(commit, incrementalOnboardingBusinessProfileDefaultState.api, state.data, rootGetters["essentials/getCountry"], key)
    },
    validateAllFields({state, dispatch}) {
        Object.keys(state.data).forEach(key => dispatch('validateField', key))
    },
    resetDirty({commit}) {
        commit('resetDirty')
    },
    updateField({commit, dispatch}, {key, event}: {key: BusinessProfileField, event: any}) {
        commit('updateField', {key, value: event.target.value})
        commit('setDirty')
        dispatch('validateField', key)
    },
    updateFieldFromEvent({commit, dispatch}, {key, event}: {key: BusinessProfileField, event: any}) {
        commit('updateField', {key, value: event.target.value})
        commit('setDirty')
        dispatch('validateField', key)
    },
    updateFieldFromValue({commit, dispatch}, {key, value}: {key: BusinessProfileField, value: any}) {
        commit('updateField', {key, value})
        commit('setDirty')
        dispatch('validateField', key)
    },
    updatePhone({commit, dispatch}, {key, value, countryCode}: {key: BusinessProfileField, value: string, countryCode: string}) {
        commit('updatePhone', {countryCode, value});
        commit('setDirty')
        dispatch('validateField', key)
    },
    copyFromFieldErrorsResponse({commit}, subErrors) {
        commit('copyFromFieldErrorsResponse', subErrors)
    },
    copyFromMerchantApiResponse({commit}, merchantApiResponse) {
        commit('copyFromMerchantApiResponse', merchantApiResponse)
    },
    copyFromPrefillData({commit}, {prefillData, merchantResponse}) {
        commit('copyFromPrefillData', {prefillData, merchantResponse})
    }
};

const mutations = {
    resetErrors(state): void {
        state.errors = {}
    },
    setErrors(state, {key, errors}): void {
        Vue.set(state.errors, key, errors);
    },
    setInvalid(state, key) {
        state.data[key].valid = false;
    },
    setValid(state, key) {
        state.data[key].valid = true;
    },
    setDirty(state) {
        state.dirty = true;
    },
    resetDirty(state) {
        state.dirty = false;
    },
    updateField(state, {key, value}) {
        state.data[key].value = value;
        delete state.errors[key];
    },
    updatePhone(state, {countryCode, value}): void {
        state.data.phone.value = `${countryCode} ${unFormatPhoneNumber(value)}`;
        state.data.phone.formattedValue = value;
        delete state.errors.phone;
    },
    copyFromFieldErrorsResponse: function (state, subErrors): void {
        state.errors = copyFromErrorResponse(subErrors, state.data)
    },
    copyFromMerchantApiResponse(state, merchantApiResponse): void {
        state.data = copyFromApiResponse(merchantApiResponse, state.data);
        if (state.data.phone.value?.includes(" ")) {
            const phoneNumber = state.data.phone.value?.split(" ")[1];
            state.data.phone.formattedValue = formatPhoneNumber(phoneNumber)
        } else {
            state.data.phone.formattedValue = formatPhoneNumber(state.data.phone.value)
        }
    },
    copyFromPrefillData(state, {prefillData, merchantResponse}: {prefillData: SessionPrefillData, merchantResponse: MerchantResponseDto}): void {
        const flatApiResponse = FlattenUtil(merchantResponse || {});
        let prefillApplied = false;
        
        
        prefillApplied = prefillField(state, BusinessProfileField.WEBSITE, flatApiResponse, prefillData.organization?.url) || prefillApplied;
        prefillApplied = prefillField(state, BusinessProfileField.PHONE, flatApiResponse, prefillData.organization?.phone) || prefillApplied;
        prefillApplied = prefillField(state, BusinessProfileField.STATEMENT_DESCRIPTOR, flatApiResponse, safeGetDomain(prefillData.organization?.url)) || prefillApplied;

        state.isPrefilled = prefillApplied;
    }
};

export const incrementalOnboardingBusinessProfile = {
    namespaced: true,
    state: incrementalOnboardingBusinessProfileDefaultState,
    getters,
    actions,
    mutations,
};
